/**
 * Extract a concise, readable title from a block of prose text.
 *
 * Strategy (in order of preference):
 * 1. If the first sentence (ending in `.`, `!`, `?`) fits within maxLength, use it.
 * 2. Otherwise, take the first N words that fit within maxLength.
 * 3. No trailing ellipsis — titles should feel intentional, not truncated.
 *
 * Fixes #1374
 */
export function extractTitle(
  text: string,
  { maxLength = 80, maxWords = 10 }: { maxLength?: number; maxWords?: number } = {},
): string {
  // Clamp maxLength to a sane minimum so ellipsis-reserve math stays safe.
  maxLength = Math.max(maxLength, 1);

  const trimmed = text.trim();
  if (!trimmed) return "Untitled";

  // Use the first line only
  const firstLine = trimmed.split(/\r?\n/)[0].trim();
  if (!firstLine) return "Untitled";

  // If the whole first line is short enough, use it as-is
  if (firstLine.length <= maxLength) {
    const words = firstLine.split(/\s+/).filter(Boolean);
    if (words.length <= maxWords) return firstLine;
  }

  // Try to use the first complete sentence.
  // We look for `.`, `!`, or `?` followed by whitespace + a new token, or
  // end-of-string. The lookahead accepts uppercase letters, digits, quotes,
  // and other common sentence-starting characters so we don't miss boundaries
  // like `…done. 3 things remain` or `…said. "Hello" she replied`.
  const sentenceEnd =
    /[.!?][)\]'"\u2018\u2019\u201C\u201D]*(?=\s+[A-Z0-9"'\u201C\u201D\u2018\u2019([]|$)/g;
  let best: string | null = null;
  let m: RegExpExecArray | null;
  while ((m = sentenceEnd.exec(firstLine)) !== null) {
    const candidate = firstLine.slice(0, m.index + m[0].length).trim();
    if (candidate.length > maxLength) break;
    // Skip `.` breaks that look like they're inside a URL.
    // Only suppress if the dot is interior to a URL token. A trailing dot on a
    // URL (e.g. "https://example.com/docs.") is treated as sentence punctuation,
    // not part of the URL itself.
    if (firstLine[m.index] === ".") {
      const tokenStart = firstLine.lastIndexOf(" ", m.index) + 1;
      const tokenEnd = firstLine.indexOf(" ", m.index);
      const token = firstLine.slice(tokenStart, tokenEnd === -1 ? undefined : tokenEnd);
      // The token looks like a URL, but the dot is its very last char —
      // that trailing dot is sentence punctuation, so allow the break.
      const isUrl = /\w:\/\/|www\./i.test(token);
      // Strip trailing closing delimiters (parentheses, brackets, quotes)
      // so that "(https://example.com/docs.)" correctly detects the dot as
      // terminal sentence punctuation rather than part of the URL.
      const stripped = token.replace(/[)\]'"'"\u201D]*$/, "");
      const dotIsTerminal = stripped.endsWith(".");
      if (isUrl && !dotIsTerminal) continue;
    }
    // Skip periods that are ordered-list markers (e.g. "1.", "2.", "10.")
    // at the beginning of the line. These are common in imported notes.
    if (firstLine[m.index] === ".") {
      const before = firstLine.slice(0, m.index);
      if (/^\d+$/.test(before.trim())) {
        continue;
      }
    }
    // Skip periods that are part of initialisms like "U.S.", "D.C.", "Ph.D."
    // Detected when the char before the dot is a single uppercase letter
    // preceded by another "X." pair (chained initialism pattern).
    if (
      firstLine[m.index] === "." &&
      m.index >= 1 &&
      /^[A-Z]$/.test(firstLine[m.index - 1]) &&
      m.index >= 2 &&
      firstLine[m.index - 2] === "."
    ) {
      continue;
    }
    // Skip periods after common honorifics/abbreviations that almost
    // always precede a name (Dr., Mr., Mrs., Prof., etc.) and dotted
    // abbreviations like e.g., i.e. that look like sentence boundaries
    // but aren't.
    //
    // Abbreviations that can also genuinely end sentences (St., Jr., Sr.,
    // vs., etc.) are NOT suppressed here — the false-negative rate is too
    // high (e.g. "Meet on Main St. Tomorrow we leave.").
    if (firstLine[m.index] === ".") {
      const before = firstLine.slice(0, m.index);
      if (
        /(?:^|[\s"'"\u201C\u201D(\[—-])(?:Dr|Mr|Mrs|Ms|Prof|Rev|Gen|Gov|Sgt|Lt|Col|Cpl|approx|e\.g|i\.e|viz|cf)$/i.test(
          before,
        )
      ) {
        continue;
      }
      // Corporate/organizational suffixes (Inc., Ltd., Corp., Co., LLC.)
      // are almost always part of an entity name rather than true sentence
      // endings, so suppress the break unconditionally. The false-negative
      // rate ("Acme Inc. The deal closed.") is lower than the false-positive
      // rate of splitting mid-name ("Acme Inc. Holdings announced…").
      if (
        /(?:^|[\s"'"\u201C\u201D(\[—-])(?:Inc|Ltd|Corp|Co|LLC|LLP|PLC|Assn|Bros)$/i.test(before)
      ) {
        continue;
      }
      // Reference abbreviations (Fig., Eq., Sec., Ch., Vol.) are only
      // non-terminal when followed by a numbered reference (e.g. "Fig. 3").
      if (/(?:^|[\s"'"\u201C\u201D(\[—-])(?:Fig|Eq|Sec|Ch|Vol)$/i.test(before)) {
        const afterDot = firstLine.slice(m.index + 1);
        if (/^\s+\d/.test(afterDot)) {
          continue;
        }
      }
      // Month abbreviations (Jan., Feb., etc.) are non-terminal when followed
      // by a numeric date (e.g. "Jan. 12"), but can end sentences otherwise.
      if (
        /(?:^|[\s"'"\u201C\u201D(\[—-])(?:Jan|Feb|Mar|Apr|Jun|Jul|Aug|Sep|Sept|Oct|Nov|Dec)$/i.test(
          before,
        )
      ) {
        const afterDot = firstLine.slice(m.index + 1);
        if (/^\s+\d/.test(afterDot)) {
          continue;
        }
      }
      // "No." is only an abbreviation when followed by a digit (e.g. "No. 5"),
      // not when used as a standalone negative ("No. We should postpone...").
      if (/(?:^|[\s"'"\u201C\u201D(\[—-])No$/i.test(before)) {
        const afterDot = firstLine.slice(m.index + 1);
        if (/^\s+\d/.test(afterDot)) {
          continue;
        }
      }
    }
    // Enforce maxWords on sentence candidates too
    const sentenceWords = candidate.split(/\s+/).filter(Boolean);
    if (sentenceWords.length > maxWords) break;
    best = candidate;
    break; // use the first valid sentence boundary
  }
  if (best) {
    return best;
  }

  // Fall back to first N words that fit within maxLength.
  // Reserve 1 char for the ellipsis so the title fits with the "…" suffix.
  const ellipsisReserve = 1; // "…".length
  const words = firstLine.split(/\s+/).filter(Boolean);
  let title = "";
  let usedAll = true;
  for (let i = 0; i < Math.min(words.length, maxWords); i++) {
    const candidate = title ? `${title} ${words[i]}` : words[i];
    if (candidate.length > maxLength - ellipsisReserve) {
      usedAll = false;
      break;
    }
    title = candidate;
  }

  // If the first token itself exceeds maxLength, truncate it rather than
  // falling back to "Untitled" for non-empty content.
  if (!title && words.length > 0) {
    return words[0].slice(0, maxLength - ellipsisReserve) + "…";
  }

  if (!title) return "Untitled";

  // Only append ellipsis when we actually truncated
  const didTruncate = !usedAll || words.length > maxWords;
  return didTruncate ? title + "…" : title;
}
